import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { isBefore, startOfYear, subYears } from 'date-fns';

import { OmaLummeApp } from '../../omaLummeContext';

import styles from './styles/LandingPage.module.scss';

import Greetings from './Greetings';
import AppCta from './AppCta';
import NewsContainer from '../News/NewsContainer';
import Box from '../Common/Box';
import Button from '../Common/Button';
import Loading from '../Common/Loading';
import LandingPageLifts from './LandingPageLiftContainer';
import ConsumptionGraph from '../Graphs/ConsumptionGraph';
import NordPoolSpotGraph from '../Graphs/NordPoolSpotGraph';
import CampaignPriceOfferBanner from './LandingPageBanners/CampaignPriceOfferBanner';
import PriceFixingBanner from './LandingPageBanners/PriceFixingBanner';
import OomiBanner from './LandingPageBanners/OomiBanner';

import { deliveryIsDiscount, deliveryIsRefund, deliveryIsVaihtoturva } from '../Contracts/ContractDetails';

// Utils.
import { gaEventIfAllowed, gaPageviewIfAllowed } from '../../utils/googleAnalytics';
import { contractRename, getContractDateTo } from '../../utils/contracts';
import { generateAddress } from '../../utils/address';
import { formatDate } from '../../utils/billing';

const LandingPage: React.FunctionComponent = () => {
  const { t } = useTranslation();

  const dispatch = OmaLummeApp.useDispatch();
  const {
    basicData,
    loadingBasicData,
    users,
    contracts,
    loadingContracts,
    offers,
    selectedMeteringPoint,
    selectedMeteringPointContract,
    landingPage,
  } = OmaLummeApp.useState();
  const { activeUser } = users;

  const [pageview, setPageview] = React.useState<boolean>(false);

  const [contractToShow, setContractToShow] = React.useState<string | undefined>();
  const [dateToShow, setDateToShow] = React.useState<string | undefined>();

  React.useEffect(() => {
    if (!pageview) {
      gaPageviewIfAllowed('/landingPage');
      setPageview(true);
    }
  }, [pageview]);

  React.useEffect(() => {
    let unMounted = false;
    async function getContractText(): Promise<void> {
      let contractDate: Date | undefined;
      let desiredDate = '';

      if (selectedMeteringPoint) {
        if (selectedMeteringPointContract) {
          contractDate = getContractDateTo(selectedMeteringPointContract).dateTo;

          if (selectedMeteringPointContract.endDate === selectedMeteringPointContract.startDate) {
            desiredDate = '';
          } else if (contractDate && contractDate < new Date()) {
            desiredDate = t('contracts:contracts.contractHasEnded');
          }
          if (contractDate && contractDate >= new Date('2073-05-30')) {
            if (activeUser.endpoint === 'ret') {
              desiredDate = t('billing:billing.forNow');
            } else {
              desiredDate = '';
            }
          }
          if (!unMounted) {
            setDateToShow(desiredDate.length > 0 ? desiredDate : contractDate ? formatDate(contractDate.toString()) : undefined);

            if (
              selectedMeteringPointContract?.activeDeliveries &&
              selectedMeteringPointContract.activeDeliveries.length > 0 &&
              selectedMeteringPointContract.activeDeliveries.find((d) => d.externalName)
            ) {
              const groupName = contractRename(
                selectedMeteringPointContract.activeDeliveries.filter(
                  (d) => !(deliveryIsVaihtoturva(d) || deliveryIsRefund(d) || deliveryIsDiscount(d)),
                )[0].externalName,
              );

              setContractToShow(groupName);
            } else {
              const lastActiveDelivery = selectedMeteringPointContract.deliveries
                ?.sort((a, b) => (a.tariffFromDate > b.tariffFromDate ? 1 : -1))
                .filter((d) => !d.externalName.toLowerCase().includes('hyvitys'))
                .pop();

              if (basicData !== undefined && contracts !== undefined && selectedMeteringPointContract.endDate && lastActiveDelivery) {
                setContractToShow(
                  selectedMeteringPointContract.startDate === selectedMeteringPointContract.endDate
                    ? t(`contracts:contracts.contractHasEnded`)
                    : lastActiveDelivery?.externalName,
                );
              } else {
                setContractToShow(t(`contracts:contracts.otherContract`));
              }
            }
          }
        } else {
          if (!loadingBasicData && !loadingContracts) {
            const basicContract = basicData?.find((d) => d.contractCode === selectedMeteringPoint.contractCode);

            if (basicContract && basicContract.contractStatus === 'S') {
              setContractToShow(`${t('contracts:contracts.contractHasEnded')}`);
            } else {
              setContractToShow(`${t('contracts:contracts.noActiveContracts')}`);
            }
          }
        }
      } else {
        if (!loadingBasicData && !loadingContracts) {
          setContractToShow(`${t('contracts:contracts.noActiveContracts')}`);
        }
      }
    }

    if (!unMounted) {
      if (activeUser.userId !== 0) {
        setDateToShow(undefined);
        setContractToShow(undefined);
        getContractText();
      } else {
        setContractToShow(`${t('contracts:contracts.noOwnAccountShort')}`);
      }
    }

    return (): void => {
      unMounted = true;
    };
  }, [activeUser.userId, activeUser.endpoint, basicData, selectedMeteringPoint, selectedMeteringPointContract, loadingBasicData, loadingContracts]);

  React.useEffect(() => {
    dispatch(['changeLandingPage', { ...landingPage, currentMonthConsumption: undefined }]);
  }, [selectedMeteringPoint]);

  let userIdentified = false;
  if (users.activeUser.userName !== '') {
    userIdentified = true;
  }

  let missingEmail = false;
  let missingNumber = false;
  if (!activeUser.userEmail) {
    missingEmail = true;
  }
  if (!activeUser.userNumber) {
    missingNumber = true;
  }

  type OmaLummeLocationState = {
    from: string;
    source: string;
  };

  let from: string | undefined = undefined;
  let source: string | undefined = undefined;

  const location = useLocation();
  const locationState = location.state as OmaLummeLocationState;
  if (locationState) {
    from = locationState.from;
    source = locationState.source;
  }

  const linkGaEvents = (gaCategory: string, gaAction: string): undefined => {
    gaEventIfAllowed({
      category: gaCategory,
      action: gaAction,
    });

    return undefined;
  };

  const meteringPointContracts = basicData ? basicData.filter((mp) => mp.GSRN === selectedMeteringPoint?.meteringPointId) : undefined;

  const selectedMeteringPointEndDate =
    basicData && basicData.length > 0
      ? basicData.find((d) => d.GSRN === selectedMeteringPoint?.meteringPointId && d.contractNo === selectedMeteringPoint.contractNo.toString())
          ?.contractEndDate
      : undefined;

  const offer =
    offers && offers.length > 0 && selectedMeteringPoint ? offers.find((o) => o.contractNo === selectedMeteringPoint.contractNo) : undefined;

  return (
    <>
      <div className={`${styles.container} ${userIdentified ? styles.identified : styles.unidentified}`}>
        {!userIdentified && <Greetings from={from} />}
        {!userIdentified && <AppCta source={source} />}
        {userIdentified && (
          <>
            <div className={styles.user}>
              <h1>
                {t('common:greetings.welcome')},<br /> {activeUser.userName}
              </h1>
            </div>
            <Box wrapper size={selectedMeteringPoint ? 'double' : ''} className={`${styles.contractBoxWrapper}`}>
              <Box wrapper size={selectedMeteringPoint ? 'twoThirds' : 'full'}>
                <OomiBanner />
                <CampaignPriceOfferBanner />
                <PriceFixingBanner />
                {!loadingBasicData &&
                  selectedMeteringPoint &&
                  meteringPointContracts &&
                  selectedMeteringPointContract?.contractType.contractTypeName.toLowerCase() === 'power' &&
                  !(selectedMeteringPointEndDate && isBefore(new Date(selectedMeteringPointEndDate), startOfYear(subYears(new Date(), 1)))) && (
                    <Box>
                      <ConsumptionGraph />
                    </Box>
                  )}
                <Box>
                  <div className={styles.contract}>
                    <div>
                      <h2 className="h1">{t('common:greetings.contracts')}</h2>
                      <div className={styles.details}>
                        {!loadingBasicData && !loadingContracts && contractToShow && (
                          <div className={styles.data}>
                            <strong>{contractToShow}</strong>
                            {selectedMeteringPointContract && getContractDateTo(selectedMeteringPointContract).dateIsLastFix === true && (
                              <span>{`${t('common:greetings.validFix')} ${dateToShow} ${t('common:greetings.until')}`}</span>
                            )}
                            {!(selectedMeteringPointContract && getContractDateTo(selectedMeteringPointContract).dateIsLastFix === true) &&
                              dateToShow &&
                              dateToShow !== t('billing:billing.forNow') &&
                              dateToShow !== t('contracts:contracts.contractHasEnded') && (
                                <span>
                                  {t('common:greetings.valid')} <em className={offer ? styles.offerDate : ''}>{dateToShow}</em>{' '}
                                  {t('common:greetings.until')}
                                </span>
                              )}
                            {dateToShow === t('billing:billing.forNow') && <span>{t('billing:billing.forNow')}</span>}
                            {dateToShow === t('contracts:contracts.contractHasEnded') && <span>{t('contracts:contracts.contractHasEnded')}</span>}
                            {offer && (
                              <a href={offer.offerUrl} className={styles.offerLink} target="_blank" rel="noopener noreferrer">
                                {offer.offerCtaOnline}
                              </a>
                            )}
                          </div>
                        )}
                        {(!contractToShow || loadingContracts || loadingBasicData) && <Loading size="small" />}

                        {basicData && selectedMeteringPoint && selectedMeteringPoint.meteringPointId && (
                          <div className={styles.data}>
                            <strong>{t(`consumption:consumption.place`)}</strong>
                            <span>
                              {generateAddress(
                                basicData.find((mp) => mp.GSRN === selectedMeteringPoint.meteringPointId)?.meteringpointAddress,
                                false,
                              )}
                            </span>
                          </div>
                        )}

                        <div className={styles.data}>
                          <strong>{`${t('common:general.customerNumber')}: ${activeUser.userId}`}</strong>
                          <span>{activeUser.userEmail}</span>
                          <span>{activeUser.userNumber}</span>
                          {missingEmail && missingNumber && (
                            <Link
                              to="/myinfo"
                              onClick={(): void => {
                                linkGaEvents('Etusivun klikkaukset', 'Yhteystietojen tarkistaminen');
                              }}
                            >
                              {t('common:greetings.missingBoth')}
                            </Link>
                          )}
                          {missingEmail && !missingNumber && (
                            <Link
                              to="/myinfo"
                              onClick={(): void => {
                                linkGaEvents('Etusivun klikkaukset', 'Yhteystietojen tarkistaminen');
                              }}
                            >
                              {t('common:greetings.missingEmail')}
                            </Link>
                          )}
                          {!missingEmail && missingNumber && (
                            <Link
                              to="/myinfo"
                              onClick={(): void => {
                                linkGaEvents('Etusivun klikkaukset', 'Yhteystietojen tarkistaminen');
                              }}
                            >
                              {t('common:greetings.missingNumber')}
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                    <div>
                      {users.activeUser.customerType !== 'business' && users.activeUser.userId !== 0 && (
                        <Button
                          link="/contracts/manage"
                          onClick={(): void => {
                            linkGaEvents('Etusivun klikkaukset', 'Tilaa e-lasku');
                          }}
                        >
                          {t('common:greetings.orderElectronicBill')}
                        </Button>
                      )}
                      <Button
                        link={
                          users.activeUser.customerType === 'business' || users.activeUser.userId === 0
                            ? '/customerService/moving'
                            : '/contracts/manage'
                        }
                        onClick={(): void => {
                          linkGaEvents('Etusivun klikkaukset', 'Oletko muuttamassa');
                        }}
                      >
                        {t('common:greetings.moving')}
                      </Button>

                      <Button
                        link="/contracts"
                        onClick={(): void => {
                          linkGaEvents('Etusivun klikkaukset', 'Katso sopimustiedot');
                        }}
                      >
                        {t('common:greetings.viewContracts')}
                      </Button>
                      <Button
                        link="/myinfo"
                        onClick={(): void => {
                          linkGaEvents('Etusivun klikkaukset', 'Päivitä tietojasi');
                        }}
                      >
                        {t('common:greetings.updateMyInfo')}
                      </Button>
                    </div>
                  </div>
                </Box>
              </Box>
              {selectedMeteringPoint && (
                <Box size="third" noStyles>
                  <LandingPageLifts />
                </Box>
              )}
            </Box>

            <Box>
              <NordPoolSpotGraph />
            </Box>
          </>
        )}
        {userIdentified && (
          <Box color="lavender">
            <h2 className="h1">{t('landingPage:news.newsTitle')}</h2>
            <NewsContainer />
          </Box>
        )}
      </div>
    </>
  );
};

export default LandingPage;
